import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Box,
  Chip,
} from '@mui/material';
import PropTypes from 'prop-types';
import facilities from '../../../config/facilities.json';
const allAdminPermissions = [
  {
    name: 'groupsAdmin',
    label: 'Groups Admin',
  },
];
const allStaffPermissions = [
  {
    name: 'activities',
    label: 'Can Edit Activities',
  },
  {
    name: 'meals',
    label: 'Can Edit Meals',
  },
  {
    name: 'memoryCareActivities',
    label: 'Can Edit Memory Care Activities',
  },
  {
    name: 'scheduler',
    label: 'Can Edit Scheduler',
  },
  {
    name: 'groupsAdmin',
    label: 'Groups Admin',
  },
];

const AccountForm = forwardRef(function (
  {
    onSubmit,
    allowAll = false,
    allowedFacilities = [],
    assignedResidents = [],
    userOnly = true,
    editMode = false,
    onCancel,
  },
  ref,
) {
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    password: '',
    passwordConfirm: '',
    role: '',
    staffPermissions: [],
    adminPermissions: [],
    facilities: [],
    firstName: '',
    lastName: '',
    assignedResidents: [],
    subRole: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  useImperativeHandle(ref, () => ({
    resetForm: (defaultFacility = '') => {
      setFormData({
        email: '',
        phone: '',
        password: '',
        passwordConfirm: '',
        role: '',
        staffPermissions: [],
        adminPermissions: [],
        facilities: defaultFacility ? [defaultFacility] : [],
        firstName: '',
        lastName: '',
        assignedResidents: [],
        subRole: '',
      });
    },
    setForm: (data) => {
      setFormData(data);
    },
  }));

  const handleChange = (prop) => (event) => {
    setFormData({ ...formData, [prop]: event.target.value });
  };

  const handleCheckboxChange = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box>
        <TextField
          label="First Name"
          type="text"
          value={formData.firstName}
          onChange={handleChange('firstName')}
          margin="normal"
          required
          sx={{ width: 1 / 2, paddingRight: 1 }}
        />
        <TextField
          label="Last Name"
          type="text"
          value={formData.lastName}
          onChange={handleChange('lastName')}
          margin="normal"
          required
          sx={{ width: 1 / 2 }}
        />
      </Box>
      <Box>
        <TextField
          label="Email"
          type="email"
          value={formData.email}
          onChange={handleChange('email')}
          margin="normal"
          required
          fullWidth
          disabled={editMode}
        />
      </Box>
      <Box>
        <TextField
          label="Phone Number"
          type="text"
          value={formData.phone}
          onChange={handleChange('phone')}
          margin="normal"
          fullWidth
        />
      </Box>
      {!editMode && (
        <div>
          <Box>
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange('password')}
              margin="normal"
              required={!editMode}
              fullWidth
              inputProps={{ minLength: 8 }}
              error={
                formData.password.length > 0 && formData.password.length < 8
              }
              helperText={
                formData.password.length > 0 &&
                formData.password.length < 8 &&
                'Password has to be longer than 8 characters!'
              }
            />
          </Box>
          <Box>
            <TextField
              label="Confirm Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.passwordConfirm}
              onChange={handleChange('passwordConfirm')}
              margin="normal"
              required={!editMode}
              fullWidth
              inputProps={{ minLength: 8 }}
              error={formData.password !== formData.passwordConfirm}
              helperText={
                formData.password !== formData.passwordConfirm &&
                'Passwords do not match!'
              }
            />
          </Box>
        </div>
      )}
      <Box>
        <FormControl fullWidth margin="normal" required>
          <InputLabel>Role</InputLabel>
          <Select
            value={formData.role}
            label="Role"
            onChange={(event) => {
              handleChange('role')(event);
              if (event.target.value === 'superadmin') {
                setFormData((prev) => ({ ...prev, facilities: [] }));
              }
            }}
          >
            {!userOnly && <MenuItem value="superadmin">Superadmin</MenuItem>}
            {!userOnly && <MenuItem value="admin">Admin</MenuItem>}
            <MenuItem value="staff">Staff</MenuItem>
            <MenuItem value="user">User</MenuItem>
            <MenuItem value="resident">Resident</MenuItem>
          </Select>
        </FormControl>
      </Box>
      {formData.role === 'admin' && (
        <Box>
          <FormControl fullWidth margin="normal">
            <InputLabel id="adminLabel">Admin permissions</InputLabel>
            <Select
              multiple
              value={formData.adminPermissions}
              onChange={handleChange('adminPermissions')}
              labelId="adminLabel"
              label="Admin permissions"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const adminPermission = allAdminPermissions.find(
                      (adminPermission) => adminPermission.name === value,
                    );
                    return (
                      <Chip
                        key={value}
                        label={adminPermission ? adminPermission.label : value}
                      />
                    );
                  })}
                </Box>
              )}
            >
              {allAdminPermissions.map((adminPermission) => (
                <MenuItem
                  key={adminPermission.name}
                  value={adminPermission.name}
                >
                  {adminPermission.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
      {formData.role === 'staff' && (
        <Box>
          <FormControl fullWidth margin="normal">
            <InputLabel id="staffLabel">Staff permissions</InputLabel>
            <Select
              multiple
              value={formData.staffPermissions}
              onChange={handleChange('staffPermissions')}
              labelId="staffLabel"
              label="Staff permissions"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const staffPermission = allStaffPermissions.find(
                      (staffPermission) => staffPermission.name === value,
                    );
                    return (
                      <Chip
                        key={value}
                        label={staffPermission ? staffPermission.label : value}
                      />
                    );
                  })}
                </Box>
              )}
            >
              {allStaffPermissions.map((staffPermission) => (
                <MenuItem
                  key={staffPermission.name}
                  value={staffPermission.name}
                >
                  {staffPermission.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
      {(formData.role === 'staff' || formData.role === 'admin') && (
        <Box>
          <FormControl fullWidth margin="normal" required>
            <InputLabel>subRole</InputLabel>
            <Select
              value={formData.subRole || ''}
              label="subRole"
              //show deselect option

              onChange={(event) => {
                if (event.target.value === 'Other') {
                  event.target.value = 'Other';
                }
                handleChange('subRole')(event);
              }}
            >
              <MenuItem value="STNA">STNA</MenuItem>
              <MenuItem value="DON">DON</MenuItem>
              <MenuItem value="Admissions">Admissions</MenuItem>
              <MenuItem value="Social Work">Social Work</MenuItem>
              <MenuItem value="Business Office">Business Office</MenuItem>
              <MenuItem value="Activities">Activities</MenuItem>
              <MenuItem value="Maintenance">Maintenance</MenuItem>
              <MenuItem value="Dietary Manager">Dietary Manager</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}
      {formData.role !== 'superadmin' && (
        <Box>
          <FormControl fullWidth margin="normal" required>
            <InputLabel id="facilityLabel">Facilities</InputLabel>
            <Select
              multiple
              value={formData.facilities}
              required
              onChange={handleChange('facilities')}
              labelId="facilityLabel"
              label="Facilities"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const facility = facilities.find(
                      (facility) => facility.id === value,
                    );
                    return (
                      <Chip
                        key={value}
                        label={facility ? facility.name : value}
                      />
                    );
                  })}
                </Box>
              )}
            >
              {facilities
                .filter(
                  (facility) =>
                    allowAll ||
                    allowedFacilities.includes(facility.id) ||
                    formData.facilities.includes(facility.id),
                )
                .map((facility) => (
                  <MenuItem
                    key={facility.id}
                    value={facility.id}
                    disabled={
                      !allowAll && !allowedFacilities.includes(facility.id)
                    }
                  >
                    {facility.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
      )}
      {formData.role == 'user' && (
        <Box>
          <FormControl fullWidth margin="normal">
            <InputLabel id="ARLabel">Assigned Residents</InputLabel>
            <Select
              multiple
              value={formData?.assignedResidents ?? []}
              onChange={handleChange('assignedResidents')}
              labelId="ARLabel"
              label="Assigned Residents"
            >
              {assignedResidents.map((resident) => (
                <MenuItem key={resident.id} value={resident.id}>
                  {resident.residentName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
      <Button type="submit" variant="contained" color="primary">
        {editMode && 'Edit'}
        {!editMode && 'Create'}
      </Button>
      {editMode && (
        <Button
          variant="contained"
          color="error"
          style={{ marginLeft: 20 }}
          onClick={() => {
            onCancel();
          }}
        >
          Clear
        </Button>
      )}
    </form>
  );
});

AccountForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  allowedFacilities: PropTypes.arrayOf(PropTypes.string).isRequired,
  allowAll: PropTypes.bool,
  userOnly: PropTypes.bool,
  editMode: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  assignedResidents: PropTypes.arrayOf(
    PropTypes.shape({ residentName: PropTypes.string, id: PropTypes.string }),
  ),
};

AccountForm.defaultProps = {
  allowAll: false,
  userOnly: true,
  editMode: false,
};

AccountForm.displayName = 'AccountForm';
export default AccountForm;
